import { useCallback, useState } from 'react'
import {
  generatePassword,
  generateUsername,
  type PasswordOptions,
  type UsernameOptions,
} from '@features/tools/services/passwordGenerator'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 入力欄インライン生成ポップアップ (#5)。
 *
 * Vault フォームの ユーザー名 / パスワード 入力欄の生成アイコンから開く。
 * 値を生成 → 「使う」で呼出側 (フォーム) のフィールドに流し込む。
 * 細かい調整はオプションで行えるが、フル機能は #tools/generator ページ。
 */

export type GeneratorPopupMode = 'username' | 'password'

export interface GeneratorPopupProps {
  mode: GeneratorPopupMode
  /** 「使う」押下時、生成値を呼出側に渡す (押下後 onClose も呼ばれる)。 */
  onUse: (value: string) => void
  onClose: () => void
}

const DEFAULT_PW: PasswordOptions = {
  length: 20,
  useUpper: true,
  useLower: true,
  useNum: true,
  useSym: true,
}

const DEFAULT_UN: UsernameOptions = {
  mode: 'word',
  wordCount: 2,
  capitalize: true,
  appendNumber: true,
  length: 12,
  emailDomain: 'gmail.com',
}

export const GeneratorPopup = ({ mode, onUse, onClose }: GeneratorPopupProps): JSX.Element => {
  const { t } = useTranslation()
  const [pwOpts, setPwOpts] = useState<PasswordOptions>(DEFAULT_PW)
  const [unOpts, setUnOpts] = useState<UsernameOptions>(DEFAULT_UN)

  const [value, setValue] = useState<string>(() =>
    mode === 'password' ? generatePassword(DEFAULT_PW) : generateUsername(DEFAULT_UN),
  )

  const regen = useCallback((): void => {
    setValue(mode === 'password' ? generatePassword(pwOpts) : generateUsername(unOpts))
  }, [mode, pwOpts, unOpts])

  // オプション変更時は即座に再生成して、表示値が常に現在のオプションを反映するようにする。
  const updatePw = (next: PasswordOptions): void => {
    setPwOpts(next)
    setValue(generatePassword(next))
  }

  const updateUn = (next: UsernameOptions): void => {
    setUnOpts(next)
    setValue(generateUsername(next))
  }

  return (
    <div
      className="vault-delete-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t(
        mode === 'password'
          ? 'tools.generator.popup.title_password'
          : 'tools.generator.popup.title_username',
      )}
      onClick={ev => {
        if (ev.target === ev.currentTarget) onClose()
      }}
    >
      <div className="vault-delete-dialog gen-popup" data-testid="generator-popup">
        <div className="gen-popup-head">
          <h3>
            {t(
              mode === 'password'
                ? 'tools.generator.popup.title_password'
                : 'tools.generator.popup.title_username',
            )}
          </h3>
          <button
            type="button"
            className="icon-btn sm"
            onClick={onClose}
            aria-label={t('common.ui.dismiss')}
          >
            ✕
          </button>
        </div>

        <div className="gen-out gen-popup-out" data-testid="generator-popup-value">
          {value}
        </div>

        <div className="gen-popup-options">
          {mode === 'password' && (
            <>
              <div className="slider-row">
                <span className="l">{t('tools.generator.options.length')}</span>
                <span className="v">{pwOpts.length}</span>
              </div>
              <input
                type="range"
                min={8}
                max={64}
                value={pwOpts.length}
                onChange={ev => updatePw({ ...pwOpts, length: Number(ev.target.value) })}
              />
              <div className="gen-options-checks">
                {(
                  [
                    { key: 'useUpper', labelKey: 'tools.generator.options.upper' },
                    { key: 'useLower', labelKey: 'tools.generator.options.lower' },
                    { key: 'useNum', labelKey: 'tools.generator.options.num' },
                    { key: 'useSym', labelKey: 'tools.generator.options.sym' },
                  ] as const
                ).map(opt => (
                  <label key={opt.key} className="check-row">
                    <input
                      type="checkbox"
                      checked={pwOpts[opt.key]}
                      onChange={ev => updatePw({ ...pwOpts, [opt.key]: ev.target.checked })}
                    />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
            </>
          )}

          {mode === 'username' && (
            <>
              <div className="field">
                <label className="field-label">{t('tools.generator.username.mode')}</label>
                <div
                  className="seg-group"
                  role="radiogroup"
                  aria-label={t('tools.generator.username.mode')}
                >
                  {(['word', 'random', 'email'] as const).map(m => {
                    const active = unOpts.mode === m

                    return (
                      <button
                        key={m}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`seg-btn${active ? ' seg-btn-active' : ''}`}
                        onClick={() => updateUn({ ...unOpts, mode: m })}
                      >
                        {t(`tools.generator.username.mode_${m}`)}
                      </button>
                    )
                  })}
                </div>
              </div>
              {unOpts.mode === 'word' && (
                <>
                  <div className="slider-row">
                    <span className="l">{t('tools.generator.username.word_count')}</span>
                    <span className="v">{unOpts.wordCount}</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    value={unOpts.wordCount}
                    onChange={ev => updateUn({ ...unOpts, wordCount: Number(ev.target.value) })}
                  />
                </>
              )}
              {(unOpts.mode === 'random' || unOpts.mode === 'email') && (
                <>
                  <div className="slider-row">
                    <span className="l">{t('tools.generator.username.length')}</span>
                    <span className="v">{unOpts.length}</span>
                  </div>
                  <input
                    type="range"
                    min={6}
                    max={32}
                    value={unOpts.length}
                    onChange={ev => updateUn({ ...unOpts, length: Number(ev.target.value) })}
                  />
                </>
              )}
              {unOpts.mode === 'email' && (
                <div className="field">
                  <label className="field-label" htmlFor="popup-email-domain">
                    {t('tools.generator.username.email_domain')}
                  </label>
                  <div className="email-domain-input-row">
                    <span className="email-domain-prefix" aria-hidden="true">
                      @
                    </span>
                    <input
                      id="popup-email-domain"
                      type="text"
                      className="input email-domain-input"
                      value={unOpts.emailDomain}
                      placeholder={t('tools.generator.username.email_domain_placeholder')}
                      autoComplete="off"
                      spellCheck={false}
                      onChange={ev =>
                        updateUn({ ...unOpts, emailDomain: ev.target.value.replace(/^@+/, '') })
                      }
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="vault-delete-actions">
          <button type="button" className="btn btn-ghost" onClick={regen}>
            <Icon name="refresh" aria-hidden="true" />
            {t('tools.generator.regen')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="generator-popup-use"
            onClick={() => {
              onUse(value)
              onClose()
            }}
          >
            {t('tools.generator.popup.use')}
          </button>
        </div>
      </div>
    </div>
  )
}
